import { useQueryClient } from '@tanstack/react-query';
import { UserKeys } from '../../../api/users-api';
import {
  isAbleToSeeUIElement,
  ToolsRoles,
} from '../../../utils/roles/client-roles';
import { Dispatch, SetStateAction } from 'react';
import { DecomModel } from '../../../../types';
import { useNavigate } from 'react-router-dom';
import { pagesConfig } from '../../../shared/pages-config';
import SharedToolbar from '../../../shared/shared-toolbar-latest';
import SharedGridStateIconButton from '../../../shared/shared-grid-state-icon-button-latest';
import { ModuleList } from '../../../shared/analytics/module-list';
import CreateButton from '../../../shared/buttons/create-button';
import SharedIconButton from '../../../shared/buttons/shared-icon-button';

interface DecomPoliciesGridToolbarProps {
  toolsRoles: string[];
  setOpenDeletePolicy: Dispatch<SetStateAction<boolean>>;
  setOpenRunDecomPolicies: Dispatch<SetStateAction<boolean>>;
  setOpenDisablePolicies: Dispatch<SetStateAction<boolean>>;
  setOpenEnablePolicies: Dispatch<SetStateAction<boolean>>;
  selectedRows: string[];
  decomModels: DecomModel[];
}

export default function DecomPoliciesGridToolbar({
  toolsRoles,
  setOpenDeletePolicy,
  setOpenRunDecomPolicies,
  setOpenDisablePolicies,
  setOpenEnablePolicies,
  selectedRows,
  decomModels,
}: DecomPoliciesGridToolbarProps) {
  const navigate = useNavigate();

  const queryClient = useQueryClient();
  const selectedDecomModels =
    decomModels.filter((model) => selectedRows.includes(model.id)) || [];
  const hasDisabledModels = selectedDecomModels.some(
    (model) => model.isActive === false
  );
  const hasEnabledModels = selectedDecomModels.some(
    (model) => model.isActive === true
  );

  return (
    <SharedToolbar
      primaryButtons={
        <CreateButton
          onClick={() =>
            navigate({
              pathname: `/${pagesConfig.decommissioningLayout.link}/${pagesConfig.createDecomPolicyFormPage.link}`,
            })
          }
          disabled={
            !isAbleToSeeUIElement(
              toolsRoles,
              ToolsRoles.CreateDecommissioningProcesses.value
            )
          }
          label="Create Policy"
          tooltipLabel={
            !isAbleToSeeUIElement(
              toolsRoles,
              ToolsRoles.CreateDecommissioningProcesses.value
            )
              ? `You are missing the ${ToolsRoles.CreateDecommissioningProcesses.name} permission to perform this action.`
              : ''
          }
        />
      }
      secondaryButtons={
        <>
          <SharedIconButton
            variant="run"
            onClick={() => setOpenRunDecomPolicies(true)}
            disabled={
              selectedRows.length === 0 ||
              !isAbleToSeeUIElement(
                toolsRoles,
                ToolsRoles.RunDecommissioningProcess.value
              ) ||
              hasDisabledModels
            }
            tooltipLabel={
              !isAbleToSeeUIElement(
                toolsRoles,
                ToolsRoles.RunDecommissioningProcess.value
              )
                ? `You are missing the ${ToolsRoles.RunDecommissioningProcess.name} permission to perform this action.`
                : hasDisabledModels
                ? 'You cannot perform a run on policies that are disabled. Please unselect or enable any disabled policies.'
                : 'Run'
            }
          />
          <SharedIconButton
            variant="block"
            onClick={() => setOpenDisablePolicies(true)}
            disabled={
              selectedRows.length === 0 ||
              !isAbleToSeeUIElement(
                toolsRoles,
                ToolsRoles.DisableDecommissioningProcesses.value
              ) ||
              hasDisabledModels
            }
            tooltipLabel={
              !isAbleToSeeUIElement(
                toolsRoles,
                ToolsRoles.DisableDecommissioningProcesses.value
              )
                ? `You are missing the ${ToolsRoles.DisableDecommissioningProcesses.name} permission to perform this action.`
                : hasDisabledModels
                ? 'You cannot disable policies that are already disabled. Please unselect any disabled policies to perform this action.'
                : 'Disable'
            }
          />
          <SharedIconButton
            variant="enable"
            onClick={() => setOpenEnablePolicies(true)}
            disabled={
              selectedRows.length === 0 ||
              !isAbleToSeeUIElement(
                toolsRoles,
                ToolsRoles.EnableDecommissioningProcesses.value
              ) ||
              hasEnabledModels
            }
            tooltipLabel={
              !isAbleToSeeUIElement(
                toolsRoles,
                ToolsRoles.EnableDecommissioningProcesses.value
              )
                ? `You are missing the ${ToolsRoles.EnableDecommissioningProcesses.name} permission to perform this action.`
                : hasEnabledModels
                ? 'You cannot enable policies that are already enabled. Please unselect any enabled policies to perform this action.'
                : 'Enable'
            }
          />
          <SharedIconButton
            variant="delete"
            onClick={() => setOpenDeletePolicy(true)}
            disabled={
              selectedRows.length === 0 ||
              !isAbleToSeeUIElement(
                toolsRoles,
                ToolsRoles.DeleteDecommissioningProcesses.value
              )
            }
            tooltipLabel={
              !isAbleToSeeUIElement(
                toolsRoles,
                ToolsRoles.DeleteDecommissioningProcesses.value
              )
                ? `You are missing the ${ToolsRoles.DeleteDecommissioningProcesses.name} permission to perform this action.`
                : 'Delete'
            }
          />
          <SharedIconButton
            variant="refresh"
            onClick={() => {
              queryClient.invalidateQueries({
                queryKey: [UserKeys.DecomModels],
              });
            }}
            tooltipLabel="Refresh"
          />
          <SharedGridStateIconButton
            gridStateKey="decomPoliciesGridStates"
            module={ModuleList.modules.automation.name}
            section={ModuleList.modules.automation.sections.decommissioning}
          />
        </>
      }
    />
  );
}
